import type { ExtractPropTypes, PropType } from 'vue'

// 进度条类型
export type ProgressType = 'line' | 'circle' | 'dashboard'

// 进度条状态
export type ProgressStatus = 'success' | 'exception' | 'warning' | 'info'

// 进度条文本位置
export type TextPosition = 'inside' | 'outside'

// 进度条尺寸
export type ProgressSize = 'large' | 'default' | 'small'

// 进度条格式化函数类型
export type FormatFunction = (percentage: number) => string

export const progressProps = {
  /**
   * 百分比，必选参数，可以通过 v-model 绑定
   */
  percentage: {
    type: Number,
    default: 0,
    required: true,
    validator: (val: number): boolean => val >= 0 && val <= 100
  },
  /**
   * 进度条类型
   */
  type: {
    type: String as PropType<ProgressType>,
    default: 'line',
    validator: (val: string): boolean => ['line', 'circle', 'dashboard'].includes(val)
  },
  /**
   * 进度条的宽度
   */
  strokeWidth: {
    type: Number,
    default: 6
  },
  /**
   * 进度条显示文字内置在进度条内（只在 type=line 时可用）
   */
  textInside: {
    type: Boolean,
    default: false
  },
  /**
   * 进度条当前状态
   */
  status: {
    type: String as PropType<ProgressStatus>,
    default: '',
    validator: (val: string): boolean => ['', 'success', 'exception', 'warning', 'info'].includes(val)
  },
  /**
   * 是否显示进度条文字内容
   */
  showText: {
    type: Boolean,
    default: true
  },
  /**
   * 进度条背景色（会覆盖状态颜色）
   */
  color: {
    type: [String, Array, Function] as PropType<
      string | string[] | ((percentage: number) => string)
    >,
    default: ''
  },
  /**
   * 环形进度条画布宽度（只在 type 为 circle 或 dashboard 时可用）
   */
  width: {
    type: Number,
    default: 126
  },
  /**
   * 是否为条纹进度条
   */
  striped: {
    type: Boolean,
    default: false
  },
  /**
   * 是否使条纹进度条动起来，仅条纹类型有效
   */
  stripedFlow: {
    type: Boolean,
    default: false
  },
  /**
   * 进度条显示文字的格式化函数
   */
  format: {
    type: Function as PropType<FormatFunction>,
    default: (percentage: number): string => `${percentage}%`
  },
  /**
   * 进度条尺寸
   */
  size: {
    type: String as PropType<ProgressSize>,
    default: 'default',
    validator: (val: string): boolean => ['large', 'default', 'small'].includes(val)
  },
  /**
   * 是否缓动动画
   */
  animated: {
    type: Boolean,
    default: false
  },
  /**
   * 进度条文字的位置
   */
  textPosition: {
    type: String as PropType<TextPosition>,
    default: 'outside',
    validator: (val: string): boolean => ['inside', 'outside'].includes(val)
  },
  /**
   * 仪表盘进度条缺口角度，可取值 0-295
   */
  gapDegree: {
    type: Number,
    default: 75,
    validator: (val: number): boolean => val >= 0 && val <= 295
  }
} as const

export type ProgressProps = Partial<ExtractPropTypes<typeof progressProps>>

export const progressEmits = {
  'update:percentage': (percentage: number) => true
}

export type ProgressEmits = typeof progressEmits